/**
 * Bridge to the Kindred Checkout Extension.
 *
 * The extension declares this origin in manifest.json → externally_connectable,
 * so `chrome.runtime.sendMessage(EXT_ID, …)` is callable from this webapp.
 *
 * If the extension is not installed (or we're in a non-Chromium browser),
 * `chrome.runtime` is undefined / sendMessage fails with
 * `"Could not establish connection. Receiving end does not exist."` — both
 * are treated as a silent no-op so the webapp keeps working standalone.
 *
 * Message types must stay in sync with
 * `packages/checkout-extension/src/auto-coupons/messages.ts → externalAuthMessages`.
 */

export const externalAuthMessages = {
  setAuthToken: "ac_setAuthToken",
  clearAuthToken: "ac_clearAuthToken",
  ping: "ac_ping",
} as const;

export interface IExtUser {
  uid?: string;
  email?: string;
  displayName?: string | null;
  role?: string | null;
}

export interface IExtPingResponse {
  ok: boolean;
  installed?: boolean;
  version?: string;
  error?: string;
}

export interface IExtAck {
  ok: boolean;
  error?: string;
}

// Minimal ambient declaration of the chrome.runtime surface we use.
// Avoids pulling @types/chrome into the webapp.
type ChromeRuntime = {
  sendMessage: (
    extensionId: string,
    message: unknown,
    responseCallback?: (response: unknown) => void
  ) => void;
  lastError?: { message?: string };
};

declare global {
  interface Window {
    chrome?: { runtime?: ChromeRuntime };
  }
}

const getChromeRuntime = (): ChromeRuntime | null => {
  if (typeof window === "undefined") return null;
  return window.chrome?.runtime ?? null;
};

const sendMessage = <T = IExtAck>(
  extensionId: string,
  message: unknown
): Promise<T | null> =>
  new Promise((resolve) => {
    const runtime = getChromeRuntime();
    if (!runtime || typeof runtime.sendMessage !== "function") {
      resolve(null);
      return;
    }
    try {
      runtime.sendMessage(extensionId, message, (response: unknown) => {
        // `lastError` is set when the extension isn't installed or didn't
        // reply. Treat as a soft failure — the extension is optional.
        if (runtime.lastError) {
          resolve(null);
          return;
        }
        resolve((response as T) ?? null);
      });
    } catch {
      resolve(null);
    }
  });

export const pingExtension = (
  extensionId: string
): Promise<IExtPingResponse | null> =>
  sendMessage<IExtPingResponse>(extensionId, {
    type: externalAuthMessages.ping,
  });

export const pushAuthTokenToExtension = (
  extensionId: string,
  token: string,
  user?: IExtUser
): Promise<IExtAck | null> =>
  sendMessage<IExtAck>(extensionId, {
    type: externalAuthMessages.setAuthToken,
    token,
    user,
  });

export const clearExtensionAuthToken = (
  extensionId: string
): Promise<IExtAck | null> =>
  sendMessage<IExtAck>(extensionId, {
    type: externalAuthMessages.clearAuthToken,
  });
